import * as THREE from "three";

interface Particle {
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  life: number;
  ttl: number;
  size: number;
  color: THREE.Color;
}

const MAX = 600;

export class Particles {
  readonly points: THREE.Points;
  private positions: Float32Array;
  private colors: Float32Array;
  private sizes: Float32Array;
  private pool: Particle[] = [];
  private active: Particle[] = [];
  private geom: THREE.BufferGeometry;

  constructor() {
    this.positions = new Float32Array(MAX * 3);
    this.colors = new Float32Array(MAX * 3);
    this.sizes = new Float32Array(MAX);

    this.geom = new THREE.BufferGeometry();
    this.geom.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    this.geom.setAttribute("color", new THREE.BufferAttribute(this.colors, 3));
    this.geom.setAttribute("size", new THREE.BufferAttribute(this.sizes, 1));
    this.geom.setDrawRange(0, 0);

    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: `
        attribute float size;
        varying vec3 vColor;
        void main() {
          vColor = color;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / -mv.z);
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: `
        varying vec3 vColor;
        void main() {
          vec2 c = gl_PointCoord - 0.5;
          float d = length(c);
          float a = smoothstep(0.5, 0.0, d);
          gl_FragColor = vec4(vColor, a);
        }
      `,
      vertexColors: true,
    });

    this.points = new THREE.Points(this.geom, mat);
    this.points.frustumCulled = false;

    for (let i = 0; i < MAX; i++) {
      this.pool.push({
        pos: new THREE.Vector3(),
        vel: new THREE.Vector3(),
        life: 0,
        ttl: 1,
        size: 1,
        color: new THREE.Color(),
      });
    }
  }

  /**
   * Streak ribbon trail — drops a small, slow-moving particle at `pos` per call.
   * Particles are world-stationary so the ship visibly flies away from them,
   * producing a ribbon read once the camera lags. Cheap (≤ 2 spawns/frame).
   */
  trail(pos: THREE.Vector3, color: THREE.Color, count = 1) {
    for (let i = 0; i < count; i++) {
      const p = this.pool.pop();
      if (!p) break;
      p.pos.copy(pos);
      p.pos.x += (Math.random() - 0.5) * 0.18;
      p.pos.y += (Math.random() - 0.5) * 0.18;
      p.vel.set(0, 0, 0);
      p.life = 0;
      p.ttl = 0.9 + Math.random() * 0.4;
      p.size = 3 + Math.random() * 2;
      p.color.copy(color);
      this.active.push(p);
    }
  }

  burst(pos: THREE.Vector3, color: THREE.Color, count = 24, speed = 6) {
    for (let i = 0; i < count; i++) {
      const p = this.pool.pop();
      if (!p) break;
      p.pos.copy(pos);
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const s = speed * (0.6 + Math.random() * 0.6);
      p.vel.set(
        Math.sin(phi) * Math.cos(theta) * s,
        Math.sin(phi) * Math.sin(theta) * s,
        Math.cos(phi) * s,
      );
      p.life = 0;
      p.ttl = 0.7 + Math.random() * 0.6;
      p.size = 6 + Math.random() * 6;
      p.color.copy(color);
      this.active.push(p);
    }
  }

  tick(dt: number) {
    let write = 0;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const p = this.active[i];
      p.life += dt;
      if (p.life >= p.ttl) {
        this.pool.push(p);
        this.active.splice(i, 1);
        continue;
      }
      p.pos.addScaledVector(p.vel, dt);
      p.vel.multiplyScalar(1 - 1.2 * dt);
    }
    for (const p of this.active) {
      const k = 1 - p.life / p.ttl;
      this.positions[write * 3] = p.pos.x;
      this.positions[write * 3 + 1] = p.pos.y;
      this.positions[write * 3 + 2] = p.pos.z;
      this.colors[write * 3] = p.color.r;
      this.colors[write * 3 + 1] = p.color.g;
      this.colors[write * 3 + 2] = p.color.b;
      this.sizes[write] = p.size * k;
      write++;
    }
    this.geom.attributes.position.needsUpdate = true;
    this.geom.attributes.color.needsUpdate = true;
    this.geom.attributes.size.needsUpdate = true;
    this.geom.setDrawRange(0, write);
  }
}
